/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { CreateEventPreview } from 'cio/utils/previews';
import { tracked } from '@glimmer/tracking';
import { Editor } from 'cio/services/editor';
import { inject as service } from '@ember/service';

import DataActionRecipientCondition from 'cio/app-models/data-action/recipient-condition';
import CreateEventActionEventName from 'cio/app-models/create-event/event-name';
import AnalyticsService from 'cio/services/analytics';
import RouterService from '@ember/routing/router-service';
import { CreateEventEditorType } from 'cio/utils/campaigns/data-action';
import DataActionBodyAttributeRows from 'cio/app-models/data-action/body-attribute-rows';
import DataActionBaseComposer, { DataActionBaseComposerArgs } from 'cio/components/action-composer/data-action/base';

interface CreateEventComposerArgs extends DataActionBaseComposerArgs {
  preview: CreateEventPreview;
}

interface CreateEventComposerSignature {
  Args: CreateEventComposerArgs;
  Element: HTMLDivElement;
}

export default class CreateEventComposer extends DataActionBaseComposer<CreateEventComposerSignature> {
  @service analytics!: AnalyticsService;
  @service router!: RouterService;

  previewObserverKeys = ['args.editableEntity.template.{body,editor,name}', 'bodyRows.isDirty'];
  previewURL = 'previews/create_event';

  @tracked recipientCondition!: DataActionRecipientCondition;
  @tracked eventName!: CreateEventActionEventName;
  @tracked bodyRows!: DataActionBodyAttributeRows;

  constructor(owner: unknown, args: CreateEventComposerSignature['Args']) {
    super(owner, args);
    this.watchTemplate();
    this.deserializeRecipientCondition();
    this.deserializeEventName();
    this.deserializeRows();
  }

  get isDirty() {
    return (
      this.template.get('hasDirtyAttributes') ||
      this.recipientCondition.isDirty ||
      this.bodyRows.isDirty ||
      this.eventName.isDirty
    );
  }

  get isInvalid() {
    if (!this.recipientCondition.isValid) {
      return true;
    }

    if (!this.eventName.isValid) {
      return true;
    }

    if (this.template.editor === Editor.DEFAULT) {
      return this.bodyRows.hasDuplicateRowNames || this.bodyRows.hasErrors;
    }

    return;
  }

  get showSaveTooltip() {
    return !this.recipientCondition.isValid || !this.eventName.isValid;
  }

  get saveTooltipContent() {
    if (!this.recipientCondition.isValid) {
      return 'You need to map the identifier to a value to find a person';
    }

    if (!this.eventName.isValid) {
      return 'You need to add an event name';
    }

    return false;
  }

  getPreviewData() {
    return {
      body: this.template.editor === Editor.DEFAULT ? this.bodyRows.serialize() : this.template.body,
      name: this.eventName.serialize(),
      recipient: this.recipientCondition.serialize(),
    };
  }

  private deserializeRecipientCondition() {
    this.recipientCondition = DataActionRecipientCondition.deserialize(this.template.recipient as string, {
      defaultType: this.defaultConditionType,
      mustMatchRecipient: this.args.editableEntity.campaign?.isDataCampaign,
    });
  }

  private deserializeEventName() {
    this.eventName = CreateEventActionEventName.deserialize(this.template.name as string);
  }

  private deserializeRows() {
    this.bodyRows = DataActionBodyAttributeRows.deserialize(this.template.body, {
      defaultType: this.defaultConditionType,
    });
  }

  @action
  async save() {
    if (this.isInvalid) {
      return;
    }

    this.template.recipient = this.recipientCondition.serialize();
    this.template.name = this.eventName.serialize();

    if (this.template.editor === Editor.DEFAULT) {
      this.template.body = this.bodyRows.serialize();
    }

    await this.args.onSave();
    this.deserializeRecipientCondition();
    this.deserializeEventName();
    this.deserializeRows();
    this.resetDirtyState();
  }

  private resetDirtyState() {
    this.args.onDirtyChange?.(false);
  }

  @action
  rollback() {
    this.template.rollbackAttributes();
    this.deserializeRecipientCondition();
    this.deserializeEventName();
    this.deserializeRows();
    this.resetDirtyState();
    this.args.onRollback();
  }

  @action
  onShowSaveButtonTooltip() {
    if (!this.isInvalid) {
      return;
    }

    this.analytics.track('user', 'error_encountered', {
      currentUrl: this.router.currentURL,
      message: this.saveTooltipContent,
      source: 'Create Event action',
    });
  }

  @action
  switchEditor(editor: CreateEventEditorType) {
    this.template.editor = editor;

    if (editor === Editor.DEFAULT) {
      this.deserializeRows();
    }
  }

  @action
  onDirtyChange(_element: HTMLElement, [isDirty]: [boolean]) {
    this.args.onDirtyChange?.(isDirty);
  }
}
